import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MyLog } from '@common/common';
import { DocumentUploadEvent } from '@data/data.module.types';
import { DataService } from '@data/data.service';
import { NotificationService } from '@notification/notification.service';
import {
  UserDeviceChangeEvent,
  UserDeviceService,
} from '@services/user-device.service';
import { dateToStr } from '@util/date-utils';
import { isFunction, isJsObject, unsub } from '@util/utils';
import { elemHeight, setElementSizes } from '@util/visual-utils';
import {
  GetMediaStreamOptions,
  getStream,
  MediaStreamWrapper,
} from '@util/web-media';
import { getClientRect } from '@util/web-utils';
import { Subscription } from 'rxjs';
import {
  DocumentUploadParams,
  DocumentUploadResult,
  OrderDataHandler,
} from '../../montage-orders/order-data-handler';
import { findBestFit, MediaCaptureTypes } from '../media.utils';
import { addCls, getFileName } from './media-capture-utils';
import { getDialogDims } from '@util/angular.utils';

const predefinedTitles: any = {
  image: 'Fotografieren',
  video: 'Video aufnehmen',
};

interface MediaRecorder {
  stop(): void;
  onstop: any;
}

const types: string[] = [
  'video/webm',
  'video/webm;codecs=vp8',
  'video/webm;codecs=vp9',
  //'video/webm;codecs=vp8.0',
  //'video/webm;codecs=vp9.0',
  //'video/webm;codecs=vp8,vorbis',
  'video/webm;codecs=h264',
  'video/webm;codecs=H264',
  'video/webm;codecs=avc1',
  'video/x-matroska;codecs=avc1',
  'video/mp4',
  'video/mp4; codecs=avc1',
  'video/mp4; codecs=avc1.4d002a',
  /*
  video/webm;codecs=vp8,opus
  video/WEBM;codecs=VP8,OPUS
  video/webm;codecs=vp9,opus
  video/webm;codecs=vp8,vp9,opus
  video/webm;codecs=h264,opus
  video/webm;codecs=h264,vp9,opus
  audio/webm
  audio/webm;codecs=opus
  */
];

function hideElem(e: any) {
  addCls(e, 'hidden');
}

function ne(elem: any) {
  return elem ? elem.nativeElement : null;
}

@Component({
  selector: 'app-media-capture-dialog',
  templateUrl: './media-capture-dialog.component.html',
})
export class MediaCaptureDialogComponent implements OnInit {
  dialogTitle = '';

  isPhoto = false;

  isVideo = false;

  isSaved = false;

  canSave = false;

  imgData: any;
  videoData: Blob | null = null;
  videoDataUrl: any;

  recordTitle = 'Aufnahme starten';

  isRecording = false;

  isUploading = false;

  progressData: any;

  recordedChunks: any[] = [];

  recordWidth = 0;
  recordHeight = 0;

  currentStream: MediaStreamWrapper | null = null;
  mediaRecorder: MediaRecorder | null = null;
  videoType = '';

  lastData: any;

  photoTitle = 'Foto machen';

  videoElemCls = 'hidden';

  orderId: any;

  fileName = '';

  types: any[] = [];

  selectedType: any;

  convert: any = {};

  imageResolutions: any = [];
  imageResId: any;
  imageTypes: any = [];
  imageTypeId: any;

  udsSub!: Subscription;

  saveBtn = new UntypedFormControl();

  _imgview!: ElementRef;
  @ViewChild('imgview') set imgview(v: ElementRef) {
    this._imgview = v;
  }

  getImg() {
    return this._imgview?.nativeElement;
  }

  _video!: ElementRef;
  @ViewChild('vs') set vs(v: ElementRef) {
    const wasEmpty = !this._video;
    this._video = v;
    wasEmpty && this.startRecording();
  }

  _actions!: ElementRef;
  @ViewChild('actions') set actions(v: ElementRef) {
    this._actions = v;
    this.setSizes();
  }

  _title!: ElementRef;
  @ViewChild('title') set title(v: ElementRef) {
    this._title = v;
    this.setSizes();
  }

  _info!: ElementRef;
  @ViewChild('info') set info(v: ElementRef) {
    this._info = v;
    //this.setSizes();
  }

  _content!: ElementRef;
  @ViewChild('cnt') set cnt(v: ElementRef) {
    this._content = v;
    this.setSizes();
  }

  _wrapper!: ElementRef;
  @ViewChild('wrapper') set wrapper(v: ElementRef) {
    this._wrapper = v;
    this.setSizes();
  }

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private userDeviceService: UserDeviceService,
    public dialogRef: MatDialogRef<MediaCaptureDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) { }

  ngOnInit(): void {
    if (Array.isArray(this.dialogData.doctypes)) {
      this.types = this.dialogData.doctypes;
      if (this.types.length) {
        this.selectedType = this.types[0];
      }
    }

    this.orderId = this.dialogData.orderId;

    const t = this.dialogData.type;

    this.isPhoto = t === MediaCaptureTypes.IMAGE;
    this.isVideo = t === MediaCaptureTypes.VIDEO;
    this.dialogTitle = t && predefinedTitles[t] ? predefinedTitles[t] : '';

    this.udsSub = this.userDeviceService
      .change()
      .subscribe((change: UserDeviceChangeEvent) => {
        this.setSizes();
        this.setPreviewVideoSizes();
      });

    if (this.isPhoto) {
      this.setupScaleParameters(this.dialogData);
    }

    setTimeout(() => {
      this.setSizes();
    }, 500);
  }

  ngOnDestroy(): void {
    this.stopRecording(false);
    this.saveBtn.disable();
    this.udsSub = unsub(this.udsSub);
  }

  setupScaleParameters(dialogData: any) {
    if (isJsObject(dialogData?.convert)) {
      const o = dialogData?.convert;
      MyLog.log('Convert config:', o);
      this.imageResolutions = o.imageResolutions;
      this.imageTypes = o.imageTypes;
      this.imageResId = o.imageResId;
      this.imageTypeId = o.imageTypeId;
      this.setupSelectedOutputResolution();
      this.setupSelectedOutputType();
    }
  }
  setupSelectedOutputResolution() {
    if (Array.isArray(this.imageResolutions)) {
      this.convert.resolution = this.imageResolutions.find((e: any) => {
        return e && e.id === this.imageResId;
      });
    }
  }

  setupSelectedOutputType() {
    if (Array.isArray(this.imageTypes)) {
      this.convert.type = this.imageTypes.find((e: any) => {
        return e && e.id === this.imageTypeId;
      });
    }
  }

  onResChange($event: any) {
    this.convert.resolution = $event;
  }

  onTypeChange($event: any) {
    this.convert.type = $event;
  }

  getContentSizes() {
    return getClientRect(this._content?.nativeElement);
  }
  innerContent() {
    return document.getElementById('mcinnercontent');
  }

  getInnerContentSizes(): any {
    return getClientRect(this.innerContent());
  }

  setSizes() {
    const content = this._content?.nativeElement;
    const wrapper = this._wrapper?.nativeElement;

    const dialogDims = getDialogDims();
    const actionDims = getClientRect(ne(this._actions));
    const titleDims = getClientRect(ne(this._title));
    //console.log('DIALOG:DIMS:', dialogDims);
    //console.log('ACTIONS:DIMS:', actionDims);
    //console.log('TITLE:DIMS:', titleDims);
    const innerContent = this.innerContent();

    if (
      actionDims &&
      actionDims.height > 0 &&
      titleDims &&
      content &&
      innerContent &&
      wrapper &&
      dialogDims &&
      dialogDims.height > 0
    ) {
      let height = dialogDims.height - actionDims.height - titleDims.height - 24;
      height = Math.floor(height);
      if (height > 0) {
        //console.log('Content:height:', height);
        elemHeight(innerContent, height + 'px');
        innerContent.style.overflow = 'hidden';
      }

      //actions.style.marginBottom = '0';
    }
  }

  close() {
    this.dialogRef.close(this.lastData);
  }

  takePhotoWithCanvas() {
    const video = this.getVideoElem();
    this.takePhotoWithCanvas2(video);
  }

  takePhotoWithCanvas2(dataSource: HTMLVideoElement | ImageBitmap) {
    const canvas = this.getPhotoElem();
    const context = canvas.getContext('2d');

    let width = dataSource.width,
      height = dataSource.height;

    if (height < this.recordHeight || width < this.recordWidth) {
      width = this.recordWidth;
      height = this.recordHeight;
    }

    if (context && width && height) {
      const img = this.getImg();

      if (img) {
        //Offset rendering
        const maxH = Math.max(10000, 2 * window.innerHeight * 4);
        const maxW = Math.max(10000, 2 * window.innerWidth * 4);

        canvas.style.position = 'fixed';
        canvas.style.top = '' + maxH + 'px';
        canvas.style.left = '' + maxW + 'px';
      }

      MyLog.log('Convert?:', this.convert);

      if (this.convert?.resolution?.browser) {
        const bestFit = findBestFit(width, height, this.convert.resolution);

        MyLog.log('Best fit:', bestFit);
        width = bestFit.width;
        height = bestFit.height;
      }

      canvas.width = width;
      canvas.height = height;
      context.drawImage(dataSource, 0, 0, width, height);

      this.imgData = {
        width: width,
        height: height,
        unit: 'px',
        data: canvas.toDataURL(),
        convert: this.convert,
      };

      if (img) {
        img.classList.remove('hidden');
        img.src = this.imgData.data;
      }

      this.checkCanSave();

      this.isSaved = true;

      canvas.classList.remove('hidden');

      this.hideVideoElem();
    }
  }

  takePhoto() {
    const canvas = this.getPhotoElem();
    const video = this.getVideoElem();

    if (this.currentStream && this.currentStream.stream && canvas && video) {
      const ImageCapture = (window as any).ImageCapture;

      if (ImageCapture) {
        const t = this.currentStream.stream.getVideoTracks();

        if (t && t.length) {
          const firstTrack = t[0];
          try {
            const ic = new ImageCapture(firstTrack);
            ic.grabFrame().then((imageBitmap: ImageBitmap) => {
              //MyLog.log('Grabbed frame:', imageBitmap);
              this.takePhotoWithCanvas2(imageBitmap);
            });
          } catch (e) {
            this.takePhotoWithCanvas();
          }
        }
        return;
      }
      this.takePhotoWithCanvas();
      return;
    }
    MyLog.error('No stream');
  }

  getPhotoElem() {
    return document.getElementById('photo') as HTMLCanvasElement;
  }

  getVideoElem() {
    return this._video?.nativeElement;
  }

  showVideoElem() {
    const elem = this.getVideoElem();
    MyLog.log('SHOW:VIDEO', elem);
    this.videoElemCls = '';
    if (elem) {
      elem.style.display = 'block';
    }
    //elem && elem.classList.remove('hidden');
  }

  hideVideoElem() {
    const elem = this.getVideoElem();
    MyLog.log('HIDE:VIDEO', elem);
    this.videoElemCls = 'hidden';
    if (elem) {
      elem.style.display = 'none';
    }
  }

  setRecordDimensions(width: number | undefined, height: number | undefined) {
    if (
      Number.isFinite(width) &&
      (width as number) > 0 &&
      Number.isFinite(height) &&
      (height as number) > 0
    ) {
      this.recordWidth = width as number;
      this.recordHeight = height as number;
    }
  }

  setPreviewVideoSizes() {
    if (!this.currentStream || !this.currentStream.stream) {
      return;
    }

    const tracks = this.currentStream.stream.getVideoTracks();

    if (tracks.length) {
      const firstTrack = tracks[0] as MediaStreamTrack;
      const trackSettings = firstTrack.getSettings();
      this.setRecordDimensions(trackSettings.width, trackSettings.height);
    }

    const video = this.getVideoElem();

    if (!video) {
      return;
    }

    if (this.recordHeight && this.recordWidth) {
      const dims = this.getPreviewSizes();

      setElementSizes(video, dims.height, dims.width);

      video.style.height = dims.height;
      video.style.width = dims.width;
    }
  }

  getPreviewSizes() {
    let h = this.recordHeight;
    let w = this.recordWidth;

    const dims = this.getInnerContentSizes();

    MyLog.log('SET RECORD DIMS:', h, w, dims);

    if (dims) {
      const infoDims = getClientRect(ne(this._info));
      let newHeight = dims.height - 10;
      let newWidth = dims.width - 24;

      if (infoDims && infoDims.height > 0) {
        newHeight -= infoDims.height + 8;
      }
      if (newHeight > 0 && newWidth > 0 && (h > newHeight || w > newWidth)) {
        const scaleH = newHeight / h;
        const scaleW = newWidth / w;
        const s = Math.min(scaleH, scaleW);
        h = s * h;
        w = s * w;
        MyLog.log('SCALE:', s, h, w);
      }
    }

    return {
      width: w,
      height: h,
    };
  }

  startRecording() {
    const vid = this.getVideoElem();
    if (!vid) {
      return;
    }
    MyLog.log('Start recording ...');

    const onSuccess = (res: any) => {
      const video = this.getVideoElem();
      this.currentStream = res;

      if (res.stream && video) {
        this.setPreviewVideoSizes();

        video.srcObject = res.stream;

        this.showVideoElem();
      }
    };

    const onError = (err: any) => {
      MyLog.log('ERR:OBJ:', err);
      err.error && MyLog.error('ERR:', err.error);
    };

    const getVideo = (video: any) => {
      getStream(
        {
          image: false,
          video: video,
        } as GetMediaStreamOptions,
        onSuccess,
        onError
      );
    };

    if (
      navigator.mediaDevices &&
      isFunction(navigator.mediaDevices.enumerateDevices)
    ) {
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          let video: any = { facingMode: "environment" };

          devices.forEach((device: any) => {
            if (
              device.kind === 'videoinput' &&
              isFunction(device.getCapabilities)
            ) {
              const caps = device.getCapabilities();
              console.log('CAP:', caps);
              if (
                caps &&
                caps.height &&
                caps.height.max &&
                caps.width &&
                caps.width.max
              ) {
                video.width = caps.width.max;
                video.height = caps.height.max;
              }
            }
          });
          getVideo(video);
        })
        .catch((error: any) => {
          getVideo(true);
        });
      return;
    }
    getVideo(true);
  }

  startMediaRecorder() {
    if (!this.currentStream) {
      return;
    }
    const MediaRecorder = (window as any).MediaRecorder;

    if (MediaRecorder) {
      let ft = '';
      const supported: any[] = [];
      for (let t of types) {
        if (MediaRecorder.isTypeSupported(t)) {
          ft = t;
          supported.push(t);
        }
      }

      this.videoType = ft;

      MyLog.log('Type found:', ft, supported);
      const options = { mimeType: ft };
      const mediaRecorder = new MediaRecorder(
        this.currentStream.stream,
        options
      );

      this.mediaRecorder = mediaRecorder;

      this.recordedChunks = [];

      mediaRecorder.ondataavailable = (event: any) => {
        //MyLog.log("data-available", event);
        if (event?.data?.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      mediaRecorder.start();
    } else {
      MyLog.log('Keine Aufnahme');
    }
  }

  stopRecording(saveVideo = true) {
    //saveVideo && this.saveVideo();
    if (this.currentStream) {
      this.currentStream.stop();
      this.currentStream = null;
    }
  }

  reset() {
    this.imgData = null;
    this.videoData = null;
    this.videoDataUrl = null;

    hideElem(this.getPhotoElem());
    hideElem(this.getImg());
    hideElem(this.getPreviewVideoElem());

    this.showVideoElem();

    this.isSaved = false;
    this.checkCanSave();
  }

  getPreviewVideoElem() {
    return document.getElementById('vid2') as HTMLVideoElement;
  }

  saveVideo() {
    if (this.mediaRecorder) {
      this.mediaRecorder.onstop = () => {
        if (this.videoType) {
          const options: any = {
            type: this.videoType,
          };
          const blob = new Blob(this.recordedChunks, options);
          this.videoData = blob;
          this.checkCanSave();

          const videoElem = this.getPreviewVideoElem();

          if (videoElem && URL) {
            const url = URL.createObjectURL(blob);
            this.videoDataUrl = url;
            videoElem.src = url;
            videoElem.classList.remove('hidden');

            this.hideVideoElem();
          }
        }
      };
      this.isSaved = true;
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    }
  }

  record() {
    this.isRecording = !this.isRecording;
    this.recordTitle = this.isRecording
      ? 'Aufnahme stoppen'
      : 'Aufnahme starten';

    if (this.isRecording) {
      this.startMediaRecorder();
    } else {
      this.saveVideo();
    }
  }

  checkCanSave() {
    if (this.isPhoto) {
      this.canSave = !!this.imgData;
    } else if (this.isVideo) {
      this.canSave = !!this.videoData;
    }
    MyLog.log('Check save', this.canSave);
    this.cdr.detectChanges();
    this.cdr.markForCheck();
  }

  save() {
    const dataHandler = OrderDataHandler.create(this.dataService);

    const params: DocumentUploadParams = {
      type: this.isPhoto ? 'image' : 'video',
      fileName: this.fileName,
      orderId: this.orderId,
      convert: this.convert,
    };

    if (this.selectedType?.id) {
      params.doctype = this.selectedType.id;
    }

    if (this.isPhoto) {
      params.image = this.imgData;
    } else {
      const ext = this.videoType.indexOf('matroska') >= 0 ? 'mkv' : 'webm';

      var fileName = getFileName(ext);

      var fileObject: any;

      if (this.videoData) {
        // we need to upload "File" --- not "Blob"
        fileObject = new File([this.videoData], fileName, {
          type: this.videoType,
        });
      }
      params.videoType = this.videoType;
      params.videoData = fileObject;
    }

    params.progressCallback = ($event: DocumentUploadEvent) => {
      if ($event && $event.type === 'IN_PROGRESS') {
        this.progressData = {
          loaded: $event.loaded,
          total: $event.total,
        };
      }
    };

    this.hideVideoElem();

    this.isUploading = true;

    dataHandler.uploadDocument(params).then((res: DocumentUploadResult) => {
      this.isUploading = false;

      if (res.success) {
        res.msg && this.notificationService.showSuccess(res.msg);
        if (this.isPhoto) {
          this.photoTitle = 'Weiteres Foto machen';
        }
        this.lastData = res;
        this.reset();
      } else {
        res.errors && this.notificationService.showErrors(res.errors);
      }
    });
  }
}
